'use client'

import { CalendarDateTime } from '@internationalized/date'
import { useForwardedRef } from 'app/lib/useFrowardedRef'
import { cn } from 'lib/utils'
import { CalendarIcon } from 'lucide-react'
import React, { useRef, useState } from 'react'
import { DateValue, I18nProvider, TimeValue, useButton, useDatePicker, useInteractOutside } from 'react-aria'
import { DatePickerStateOptions, useDatePickerState } from 'react-stately'
import { Button } from '../button'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { Calendar } from './calender'
import { DateField } from './date-field'
import { TimeField } from './time-field'

const DateTimePicker = React.forwardRef<HTMLDivElement, DatePickerStateOptions<DateValue>>((props, forwardedRef) => {
    const ref = useForwardedRef(forwardedRef)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)
    const [open, setOpen] = useState(false)

    const state = useDatePickerState({
        ...props,
        shouldCloseOnSelect: false
    })

    const {
        groupProps,
        fieldProps,
        buttonProps: _buttonProps,
        dialogProps,
        calendarProps
    } = useDatePicker(props, state, ref)

    const { buttonProps } = useButton(_buttonProps, buttonRef)

    useInteractOutside({
        ref: contentRef,
        onInteractOutside: () => {
            if (open) {
                setOpen(false)
            }
        }
    })

    const handleTimeChange = (time: TimeValue | null) => {
        if (state.value && time) {
            const currentDate = state.value as CalendarDateTime
            const updatedDate = currentDate.set({
                hour: time.hour,
                minute: time.minute
            })
            props.onChange?.(updatedDate)
        }
    }

    return (
        <div
            {...groupProps}
            ref={ref}
            className={cn(
                groupProps.className,
                'flex items-center rounded-md ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2'
            )}
        >
            <label htmlFor='date-time-picker' className='sr-only'>
                {props.label}
            </label>
            <I18nProvider locale='en-GB'>
                <DateField {...fieldProps} id='date-time-picker' />
            </I18nProvider>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        {...buttonProps}
                        variant='outline'
                        className='rounded-l-none'
                        disabled={props.isDisabled}
                        onClick={() => setOpen(true)}
                    >
                        <CalendarIcon className='h-5 w-5' />
                    </Button>
                </PopoverTrigger>
                <PopoverContent ref={contentRef} className='w-full'>
                    <div {...dialogProps} className='space-y-3'>
                        <I18nProvider locale='en-GB'>
                            <Calendar {...calendarProps} />
                        </I18nProvider>
                        {!!state.hasTime && (
                            <I18nProvider locale='en-US'>
                                <TimeField
                                    aria-label='time'
                                    value={state.timeValue}
                                    onChange={(time: TimeValue | null) => time && handleTimeChange(time)}
                                    granularity='minute'
                                />
                            </I18nProvider>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
})

DateTimePicker.displayName = 'DateTimePicker'

export { DateTimePicker }
